import { api } from './api';

/**
 * Register the Service Worker, ask for notification permission, and POST
 * the push subscription to the backend. Idempotent — if everything's
 * already in place this resolves quickly without prompting the user.
 *
 * Returns a friendly status the caller can surface in the UI:
 *   - 'subscribed'      — push is wired up
 *   - 'denied'          — user blocked notifications; can't fix from JS
 *   - 'unsupported'     — browser lacks the APIs (Safari pre-16, etc)
 *   - 'no-key'          — backend hasn't shipped a VAPID public key
 *   - 'error'           — something else went wrong
 */
export type WebPushStatus = 'subscribed' | 'denied' | 'unsupported' | 'no-key' | 'error';

let registrationPromise: Promise<WebPushStatus> | null = null;

export async function sendTestPush(): Promise<{ ok: boolean; message: string }> {
  try {
    const r = await fetch('/api/push/web/test', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, message: j.message || `${r.status}` };
    return { ok: true, message: `Test sent to ${j.subscriptions ?? 0} subscription(s).` };
  } catch (e) {
    return { ok: false, message: (e as Error).message };
  }
}

export function setupWebPush(): Promise<WebPushStatus> {
  if (registrationPromise) return registrationPromise;
  // Listen for SW pushsubscriptionchange relays — Chrome silently rotates
  // subscriptions occasionally, leaving our server with a stale endpoint
  // that returns 201 but never reaches the browser. When the SW signals
  // a rotation, drop the cached registration so the next call re-runs
  // the subscribe flow with the fresh endpoint.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data?.type === 'pushsubscriptionchange') {
        // eslint-disable-next-line no-console
        console.log('[push] subscription rotated — re-running setupWebPush');
        registrationPromise = null;
        void setupWebPush();
      }
      // Clicking a push-delivered OTP notification: the SW relays the
      // notification data and focuses this tab. Copy JUST the OTP code
      // (never the SMS body) so the user can paste it straight in.
      if (e.data?.type === 'notification_click') {
        const code = e.data.data?.otpCode;
        if (typeof code === 'string' && code) {
          void navigator.clipboard.writeText(code).catch(() => {});
        }
      }
      // Auto-copy from the SW BEFORE the banner appears. Fired for every
      // OTP push, to every controlled window; the browser only permits
      // the clipboard write from the currently-focused tab, so a user
      // with the app in front gets the code without touching the banner.
      // Background tabs silently fail — no harm done. Never leaks the
      // SMS body, only the numeric code.
      if (e.data?.type === 'otp_auto_copy') {
        const code = e.data.code;
        if (typeof code === 'string' && code) {
          void navigator.clipboard.writeText(code).catch(() => {});
        }
      }
    });
  }
  registrationPromise = (async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return 'unsupported';
    }

    let reg: ServiceWorkerRegistration;
    try {
      reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      if (reg.installing) await waitForState(reg.installing, 'activated');
    } catch {
      return 'error';
    }

    const perm = await Notification.requestPermission();
    if (perm === 'denied') return 'denied';
    if (perm !== 'granted') return 'error';

    let publicKey: string | null = null;
    try {
      const r = await api<{ publicKey: string | null }>('/api/push/web/key');
      publicKey = r.publicKey;
    } catch {
      return 'error';
    }
    if (!publicKey) return 'no-key';

    let sub: PushSubscription | null = null;
    try {
      sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
    } catch {
      return 'error';
    }

    try {
      const j = sub.toJSON();
      await api('/api/push/web/subscribe', {
        method: 'POST',
        body: {
          endpoint: j.endpoint!,
          keys: { auth: j.keys!.auth, p256dh: j.keys!.p256dh },
          userAgent: navigator.userAgent.slice(0, 500),
        },
      });
    } catch {
      return 'error';
    }
    return 'subscribed';
  })();
  return registrationPromise;
}

function waitForState(worker: ServiceWorker, state: ServiceWorkerState): Promise<void> {
  return new Promise((resolve) => {
    if (worker.state === state) return resolve();
    worker.addEventListener('statechange', () => {
      if (worker.state === state) resolve();
    });
  });
}

function urlBase64ToUint8Array(base64: string): BufferSource {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}
